import * as Yup from 'yup'
import { priceSchema, time } from 'utils/form'
import { OrderPointType } from './OrderPointsForm'
import { parseCoordinates } from 'utils/utils'
import { parseMomentFromLocalDateAndLocalTimeFields } from 'utils/date'
import {CarrierSchema} from "./CarrierForm";

function validateFromTimeBeforeToTime (_, { parent }) {
  const parsedFromDate = parseMomentFromLocalDateAndLocalTimeFields(
    parent.fromDate, parent.fromTime)
  const parsedToDate = parseMomentFromLocalDateAndLocalTimeFields(
    parent.toDate, parent.toTime)
  return parsedFromDate.isSameOrBefore(parsedToDate)
}

const deliveryPointSchema = Yup.object().shape({
  fromDate: Yup.string()
    .test('from-date',
      'form.validation.deliveryPointFromDateTime', validateFromTimeBeforeToTime),
  fromTime: time()
    .test('from-date', ' ', validateFromTimeBeforeToTime),
  toDate: Yup.string(),
  toTime: time(),
  companyName: Yup.string(),
  cityName: Yup.string()
    .required('form.validation.required'),
  country: Yup.string()
    .required('form.validation.required')
    .nullable(),
  address: Yup.string(),
  zipCode: Yup.string()
    .required('form.validation.required'),
  additionalInfo: Yup.string(),
  optionalFeatures: Yup.array(),
  cargoDetails: Yup.object().shape({
    name: Yup.string(),
    wrappingType: Yup.string(),
    quantity: Yup.number(),
    weight: Yup.number(),
    dispenser: Yup.string()
  })
})

const ferryPassageSchema = Yup.object().shape({
  contractorId: Yup.string().nullable().required(),
  ferryServiceId: Yup.string().nullable().required(),
  harbourCoordinates: Yup.string().required()
    .test('coordinates', 'form.validation.coordinates', text => {
      if (text) {
        const coordinates = parseCoordinates(text)
        return coordinates != null
      }
      return true
    }),
  price: priceSchema.required(),
  paymentDays: Yup.number().required(),
  bunkerAdjustmentFactor: priceSchema.required()
})

const addressSchema = Yup.object({
  address: Yup.string(),
  city: Yup.string().required(),
  zipCode: Yup.string().required(),
  countryCode: Yup.string().required()
})

const customsControlSchema = Yup.object({
  customsAgencyName: Yup.string().required(),
  address: addressSchema.required(),
  contractPersonName: Yup.string(),
  additionalInfo: Yup.string()
})

const customIntermediatePointSchema = Yup.object({
  description: Yup.string().required(),
  address: addressSchema.required()
})

const orderPointSchema = Yup.object().shape({
  deliveryPointDetails: Yup.object().when('type', {
    is: type => type === OrderPointType.Loading || type === OrderPointType.Unloading,
    then: deliveryPointSchema.required()
  }),
  ferryPassageDetails: Yup.object().when('type', {
    is: OrderPointType.FerryPassage,
    then: ferryPassageSchema.required()
  }),
  customsControlDetails: Yup.object().when('type', {
    is: OrderPointType.CustomsControl,
    then: customsControlSchema.required()
  }),
  customIntermediatePointDetails: Yup.object().when('type', {
    is: OrderPointType.Custom,
    then: customIntermediatePointSchema.required()
  })
})

const transactionSchema =
    Yup.object().shape({
      price: priceSchema.required(),
      contractorId: Yup.string().required(),
      paymentDays: Yup.number().required(),
      serviceTypeId: Yup.string().required(),
      comment: Yup.string()
    })

const orderTransactionSchema = {
  incomes: Yup.array().of(transactionSchema),
  costs: Yup.array().of(transactionSchema)
}

export const OrderSchema = Yup.object().shape({
  referenceId: Yup.string(),
  points: Yup.array()
    .of(orderPointSchema)
    .test('atLeastOneLoadingDeliveryPoint', 'form.validation.loadingDeliveryPoints.required', points =>
      points.some(p => p.type === OrderPointType.Loading)
    )
    .test('atLeastOneUnloadingDeliveryPoint', 'form.validation.unloadingDeliveryPoints.required', points =>
      points.some(p => p.type === OrderPointType.Unloading)
    ),
  contractorInfo: Yup.object().shape({
    clientId: Yup.string()
      .nullable()
      .required('form.validation.required'),
    payerId: Yup.string()
      .nullable()
      .required('form.validation.required'),
    emails: Yup.array()
      .required('form.validation.required')
      .min(1, 'form.validation.required'),
    bccEmails: Yup.array()
  }),
  emailNotificationSettings: Yup.object().shape({
    frequencySettingsType: Yup.string(),
    sendIntervalHours: Yup.string().nullable(),
    language: Yup.string()
  }),
  attachmentsIds: Yup.array()
    .of(Yup.string()),
  deletedAttachmentsIds: Yup.array()
    .of(Yup.string()).nullable(),
  transactions: Yup.object().shape(orderTransactionSchema),
  carrier: CarrierSchema,
  forwarderId: Yup.string()
    .nullable()
    .required('form.validation.required'),
  dispatcherId: Yup.string()
    .nullable(),
  comment: Yup.string(),
  printComment: Yup.string(),
  highValue: Yup.boolean()
})
